import { FunctionComponent } from 'react';
import clsx from 'clsx';
import styles from './TransportationDataInfo.module.scss';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { DataVaraible, AppointmentMultiple, PetServiceWrapper, itemTypes, ItemData } from 'model/NewMultipleBooking';
import { Product } from '@spike/product-model';
import AddOnService from '@spike/addon-service-model';
import Service from 'model/Service';
import { Moment } from 'moment-timezone';
import ServiceOptions from './ServiceOptions';
import Staff from 'model/Staff';
import { getPriceWithDiscount } from './ServiceOrComplementaryDataInfo';
import { resolveAppointmentCardLineBase, resolveAppointmentCardLineDisplay } from '../utils/membershipDisplayPrice';

interface Props {
    data: DataVaraible;
    index: number;
    appts: Array<DataVaraible>;
    appointment: AppointmentMultiple;
    onEdit: (
        petId: number,
        dateEdit: Moment,
        staffId?: number,
        itemData?: ItemData,
        service?: PetServiceWrapper,
        product?: Product,
        addOn?: AddOnService,
        custom?: string,
        type?: itemTypes,
        dateEnd?: Moment
    ) => void;
    onDeleteAppointment: (petId: number, value?: number | string, type?: itemTypes, dateToRemove?: Moment) => void;
    onShowActions: (
        petId: number,
        dateEdit: Moment,
        itemData?: ItemData,
        service?: PetServiceWrapper,
        staffId?: number,
        product?: Product,
        addOn?: AddOnService,
        custom?: string,
        type?: itemTypes,
        dateEnd?: Moment
    ) => void;
}

export const TransportationDataInfo: FunctionComponent<Props> = props => {
    const staffs = useSelector<RootState, Array<Staff>>(state => state.staff.staff);
    const services = useSelector<RootState, Array<Service>>(state => state.services.services);
    const staff = staffs?.find((staff: Staff) => staff.id === props.data.staffId);

    const slotLinePrice = Number(props.data.service?.price) || 0;
    const lineBase = resolveAppointmentCardLineBase({
        slotPrice: slotLinePrice,
        itemDataPrice: props.data.itemData?.price,
        beforePrice: props.data.service?.beforePrice,
        customPriceAmount: props.data.service?.customPriceAmount
    });
    const {
        displayPrice: resolvedDisplayPrice,
        originalPrice: displayPriceBeforeDiscount,
        hasDisplayDiscount
    } = resolveAppointmentCardLineDisplay({
        enrollmentId: props.data.service?.enrollment?.id,
        membershipItem: props.data.service?.membershipItem,
        slotPrice: slotLinePrice,
        basePrice: lineBase,
        itemDiscount: props.data.itemData?.discount ?? props.data.service?.discount
    });

    return (
        <>
            {props.index === 0 && (
                <div className={styles.dateContainer}>
                    <span className={styles.date}>{props.data.date?.format('dddd, DD MMM YYYY')}</span>
                </div>
            )}
            <div
                className={styles.serviceStaffContainer}
                key={props.data.service?.service?.id}
            >
                <div className={styles.itemContainer}>
                    <div
                        className={styles.emptyBox}
                        style={{
                            borderLeftColor:
                                services.find((serv: Service) => serv.id === props.data.service?.service?.id)?.color ??
                                '#ffffff'
                        }}
                    />
                    <div
                        className={clsx(styles.divide, {
                            [styles.withoutDivider]: props.index === props.appts.length - 1
                        })}
                        style={{ width: '91%' }}
                    >
                        <span className={styles.serviceName}>
                            <span className={clsx(styles.serviceNameText, styles.rightSpace)}>
                                {props.data.service?.service?.name}
                            </span>
                            {props.data.service ? (
                                <>
                                    <div className={styles.serviceData}>
                                        {hasDisplayDiscount && (
                                            <span className={clsx(styles.beforePrice, styles.price)}>
                                                ${displayPriceBeforeDiscount.toFixed(2)}
                                            </span>
                                        )}
                                        {hasDisplayDiscount && (
                                            <span className={styles.price}>${resolvedDisplayPrice.toFixed(2)}</span>
                                        )}
                                    </div>
                                    {!hasDisplayDiscount && (
                                        <span className={styles.price}>{'$' + resolvedDisplayPrice.toFixed(2)}</span>
                                    )}
                                </>
                            ) : (
                                <div>
                                    {props.data?.itemData?.discount?.value === undefined ||
                                    props.data?.itemData?.discount?.value === 0 ? (
                                        <span className={styles.price}>${props.data?.itemData?.total?.toFixed(2)}</span>
                                    ) : (
                                        <div className={styles.serviceData}>
                                            <span className={clsx(styles.beforePrice, styles.price)}>
                                                ${props.data.itemData?.total?.toFixed(2)}
                                            </span>
                                            <span className={styles.price}>
                                                $
                                                {getPriceWithDiscount(props.data, props.data.itemData?.total)?.toFixed(
                                                    2
                                                )}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </span>
                        <div className={styles.serviceDataContainer}>
                            <div className={styles.dataContainer}>
                                {props.data.service?.service && (
                                    <span className={styles.priceAndDuration}>
                                        {`${props.data.date?.format('LT')} - ${props.data.dateEnd?.format('LT')}`}
                                        <span className={styles.dot}>·</span>
                                    </span>
                                )}
                                <div className={styles.dataContainer}>
                                    <span className={clsx(styles.priceAndDuration, styles.spacingDuration)}>
                                        {props.data.itemData?.type === 'pickup' ? 'Pick-up' : 'Drop-off'}
                                    </span>
                                </div>
                                <span className={styles.priceAndDuration}>
                                    <span className={styles.dot}>·</span>
                                    {`${staff ? staff?.person?.firstName : 'Unassigned'} (${
                                        props.data.service?.vehicle?.name
                                    })`}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div
                        className={clsx(styles.divide, styles.serviceOptionsContainer, {
                            [styles.withoutDivider]: props.index === props.appts.length - 1
                        })}
                    >
                        <ServiceOptions
                            className={styles.serviceOptions}
                            item="Service"
                            onClickMobile={() =>
                                props.onShowActions(
                                    props.appointment.petId,
                                    props.data.date,
                                    props.data.itemData,
                                    props.data.service,
                                    props.data.staffId,
                                    props.data.product,
                                    props.data.addOn,
                                    props.data.customDescription,
                                    itemTypes.service,
                                    props.data.dateEnd
                                )
                            }
                            onEdit={() =>
                                props.onEdit(
                                    props.appointment.petId,
                                    props.data.date,
                                    props.data.staffId,
                                    props.data.itemData,
                                    props.data.service,
                                    props.data.product,
                                    props.data.addOn,
                                    props.data.customDescription,
                                    itemTypes.service,
                                    props.data.dateEnd
                                )
                            }
                            onDeleteAppointment={() =>
                                props.onDeleteAppointment(
                                    props.appointment.petId,
                                    props.data.service?.service?.id,
                                    itemTypes.service,
                                    props.data.date
                                )
                            }
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

export default TransportationDataInfo;
